import {
  useSiteReducedMotion,
  useScrollDirection,
  useForwardProgress,
} from "./motion-preferences";
import {
  motion,
  useScroll,
  useTransform,
  useInView,
  useAnimationControls,
} from "framer-motion";
import { useRef, useEffect } from "react";
import { ArrowUpRight } from "lucide-react";

const revealHidden = { opacity: 0, y: 38, filter: "blur(4px)" };
const revealVisible = { opacity: 1, y: 0, filter: "blur(0px)" };
export function Reveal({ children, className = "", delay = 0 }) {
  const direction = useScrollDirection();
  const reduced = useSiteReducedMotion(),
    ref = useRef(null),
    controls = useAnimationControls();
  const entered = useInView(ref, { amount: 0.12 });
  const nearby = useInView(ref, { margin: "100px 0px", amount: 0 });
  useEffect(() => {
    if (reduced || direction === "up") {
      controls.stop();
      controls.set(revealVisible);
    } else if (entered) controls.start(revealVisible);
    else if (!nearby) controls.set(revealHidden);
  }, [reduced, direction, entered, nearby, controls]);
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={reduced || direction === "up" ? false : revealHidden}
      animate={controls}
      onFocusCapture={() => controls.set(revealVisible)}
      transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}
export function Eyebrow({ children, number }) {
  return (
    <div className="eyebrow">
      <span className="section-dot" />
      {children}
      {number && <span className="section-number">/{number}</span>}
    </div>
  );
}
export function ActionLink({ children, href = "#contact", className = "" }) {
  return (
    <a href={href} className={`action-link ${className}`}>
      <span>{children}</span>
      <span className="action-icon">
        <ArrowUpRight size={19} aria-hidden="true" />
      </span>
    </a>
  );
}
export function BrandLogo() {
  return (
    <img
      className="brand-logo"
      src="/logo.svg"
      alt="QuantumCode"
      width="205"
      height="32"
    />
  );
}

export function ScrollHeading({ lines, className = "" }) {
  const direction = useScrollDirection();
  const reduced = useSiteReducedMotion(),
    ref = useRef(null),
    controls = useAnimationControls();
  const entered = useInView(ref, { amount: 0.45 });
  const nearby = useInView(ref, { margin: "100px 0px", amount: 0 });
  useEffect(() => {
    if (reduced || direction === "up") {
      controls.stop();
      controls.set("visible");
    } else if (entered) controls.start("visible");
    else if (!nearby) controls.set("hidden");
  }, [reduced, direction, entered, nearby, controls]);
  return (
    <motion.h2
      ref={ref}
      className={`scroll-heading ${className}`}
      aria-label={lines
        .map((line) => (line.before || "") + line.text)
        .join(" ")}
      initial={reduced || direction === "up" ? false : "hidden"}
      animate={controls}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.11 } },
      }}
    >
      {lines.map((line, i) => (
        <span className="heading-mask" key={i} aria-hidden="true">
          <motion.span
            variants={{
              hidden: { y: "105%", rotate: 2 },
              visible: {
                y: "0%",
                rotate: 0,
                transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
              },
            }}
          >
            {line.before}
            <span className={line.muted ? "muted" : ""}>{line.text}</span>
          </motion.span>
        </span>
      ))}
    </motion.h2>
  );
}
export function ScrollDepth({ children, className = "", distance = 20 }) {
  const ref = useRef(null),
    reduced = useSiteReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const progress = useForwardProgress(scrollYProgress);
  const y = useTransform(progress, [0, 0.5, 1], [distance, 0, -distance]);
  return (
    <div ref={ref} className={`scroll-depth ${className}`}>
      <motion.div style={{ y: reduced ? 0 : y }}>{children}</motion.div>
    </div>
  );
}

export function useSceneActivity() {
  const ref = useRef(null);
  const visible = useInView(ref, { amount: 0.05 });
  const reduced = useSiteReducedMotion();
  const direction = useScrollDirection();
  return { ref, active: visible && !reduced && direction === "down" };
}
